// Per-device chat UX preferences. Persisted in localStorage and broadcast via
// a custom event so any component can re-read on update without prop drilling.

import { useEffect, useState } from 'react';

export type FontSize = 'small' | 'medium' | 'large';
export type Wallpaper = 'default' | 'indigo' | 'forest' | 'sand' | 'mono' | 'aurora';

export interface ChatPrefs {
  enterToSend: boolean;
  spellcheck: boolean;
  showLinkPreviews: boolean;
  animations: boolean;
  fontSize: FontSize;
  wallpaper: Wallpaper;
}

export const DEFAULTS: ChatPrefs = {
  enterToSend: true,
  spellcheck: true,
  showLinkPreviews: true,
  animations: true,
  fontSize: 'medium',
  wallpaper: 'default',
};

const KEY = 'chat:chat-prefs';
const EVENT = 'chat:prefs-changed';

export function loadChatPrefs(): ChatPrefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULTS;
    return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<ChatPrefs>) };
  } catch {
    return DEFAULTS;
  }
}

export function saveChatPrefs(p: ChatPrefs): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent<ChatPrefs>(EVENT, { detail: p }));
  applyChatPrefs(p);
}

// Apply visual prefs (font size, wallpaper) as CSS custom properties on the
// document root so any chat surface can pick them up via `var(--…)`.
export function applyChatPrefs(p: ChatPrefs): void {
  const root = document.documentElement;
  const sizeMap: Record<FontSize, string> = {
    small: '13px',
    medium: '14px',
    large: '16px',
  };
  root.style.setProperty('--chat-message-size', sizeMap[p.fontSize]);
  root.dataset.wallpaper = p.wallpaper;
}

export function useChatPrefs(): ChatPrefs {
  const [prefs, setPrefs] = useState<ChatPrefs>(loadChatPrefs);
  useEffect(() => {
    function onCustom(e: Event) {
      const ce = e as CustomEvent<ChatPrefs>;
      if (ce.detail) setPrefs(ce.detail);
    }
    function onStorage(e: StorageEvent) {
      if (e.key === KEY) setPrefs(loadChatPrefs());
    }
    window.addEventListener(EVENT, onCustom);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT, onCustom);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  return prefs;
}
